import { Dictionary } from 'lodash';
import { useCallback } from 'react';

import { useUpdateUserSettings } from '~/components/UserSettings';
import { useUpdateCompanyPreferences } from '~/data/companyPreferences/useUpdateCompanyPreferences';
import { WorkspaceType } from '~/scenes/MyWorkspace/data';
import { WorkspacesSettings } from '~/scenes/MyWorkspace/hooks';
import {
  CompanyWorkspaceSettingsKey,
  MyWorkspaceUserSettingsKey,
} from '~/scenes/MyWorkspace/userSettings';

type UseUpdateWorkspaceSettingsOutput = {
  updateWorkspacesSettings: (payload: WorkspacesSettings) => void;
};

export const useUpdateWorkspaceSettings =
  (): UseUpdateWorkspaceSettingsOutput => {
    const updateUserSettings = useUpdateUserSettings();
    const updateCompanyPreferences = useUpdateCompanyPreferences();

    const wrapWorkspacesDataInSettingsKey = useCallback(
      (
        data: WorkspacesSettings,
        settingsKey: string,
      ): Dictionary<WorkspacesSettings> => {
        return {
          [settingsKey]: {
            ...data,
          },
        };
      },
      [],
    );

    const updateWorkspacesSettings = useCallback(
      (payload: WorkspacesSettings) => {
        const { workspaces, activeWorkspaceId } = payload;

        const getWorkspacesByType = (type: WorkspaceType) => {
          const result = workspaces.filter((ws) => ws.type === type);

          return {
            workspaces: result,
          };
        };

        // Update to different places based on workspace type: Company | Personal
        updateCompanyPreferences(
          wrapWorkspacesDataInSettingsKey(
            {
              ...getWorkspacesByType(WorkspaceType.Company),
            },
            CompanyWorkspaceSettingsKey,
          ),
        );
        updateUserSettings(
          wrapWorkspacesDataInSettingsKey(
            {
              ...getWorkspacesByType(WorkspaceType.Personal),
              activeWorkspaceId,
            },
            MyWorkspaceUserSettingsKey,
          ),
        );
      },
      [
        updateUserSettings,
        updateCompanyPreferences,
        wrapWorkspacesDataInSettingsKey,
      ],
    );

    /**
     * Function clearAllWorkspacesSettings() is not intended for actual users.
     * Stored here in case needed in development for easy clearing.
     */
    /* const clearAllWorkspacesSettings = () => {
      const noWorkspaces = { workspaces: [] };

      updateCompanyPreferences(
        wrapInWorkspaceKey(noWorkspaces, CompanyWorkspaceSettingsKey),
      );
      updateUserSettings(
        wrapInWorkspaceKey(
          {
            ...noWorkspaces,
            activeWorkspaceId: '',
          },
          MyWorkspaceUserSettingsKey,
        ),
      );
    };*/

    return {
      updateWorkspacesSettings,
    };
  };
